import { createApi } from '@reduxjs/toolkit/query/react';
import { filteredFilter } from '../../utils/api';
import {properties} from "../../properties.js";
import { authBaseQuery } from '../../app/axiosBaseQuery';
import { PaginationResult, ListRequest } from '../../types/global.d';
import * as Entity from '../types/models/Shipment';
import {createNormalizationMiddleware} from "../../app/normy/normyRtkQuery.ts";

export const SHIPMENT_API_REDUCER_KEY = 'shipments';
export const SHIPMENT_PATH = 'shipments';

export const shipmentApi = createApi({
    reducerPath: SHIPMENT_API_REDUCER_KEY,
    baseQuery: authBaseQuery(),
    keepUnusedDataFor: 10,
    refetchOnMountOrArgChange: 10,
    refetchOnFocus: true,
    tagTypes: ['Shipment', 'Shipments'],
    endpoints: (builder) => ({
        getShipmentList: builder.query<PaginationResult<Entity.Shipment>, ListRequest>({
            query: (request) => {
                const queryParams: Record<string, any> = Object.assign({}, filteredFilter(request))
                return ({
                    url: `/${ SHIPMENT_PATH }?${new URLSearchParams(queryParams).toString()}`,
                    method: 'GET',
                })
            },
            providesTags: ['Shipments'],
        }),
        getShipment: builder.query<Entity.Shipment, Entity.PrimaryShipment>({
            queryFn: async (id, { getState }, extraOptions, baseQuery) => {

                const cachedData = shipmentNormalizer.getObjectById(id.id);

                if (cachedData) {
                    return { data: cachedData };
                } else {
                    const response = await baseQuery({
                        url: `/${ SHIPMENT_PATH }/${id.toPath()}`,
                        method: 'GET',
                    });

                    return response;
                }
            },
            providesTags: ['Shipment'],
        }),
        getShipmentMultiple: builder.query<Entity.Shipment, Entity.PrimaryShipment[]>({
            query: (ids) => {
                return ({
                    url: `/${ SHIPMENT_PATH }/ids${Entity.PrimaryShipment.toQueryParams(ids)}`,
                    method: 'GET',
                })
            },
            providesTags: ['Shipments'],
        }),
        getShipmentPresentation: builder.query<PaginationResult<Entity.Shipment>, Entity.PresentationShipmentRequest>({
            query: (request) => {
              const queryParams: Record<string, any> = Object.assign({}, filteredFilter(request.list))
              return ({
                url: `/${ SHIPMENT_PATH }/presentation?${new URLSearchParams(queryParams).toString()}`,
                method: 'POST',
                data: request.filter,
              })
            },
            providesTags: ['Shipments'],
        }),
        getShipmentAdvancedPresentation: builder.query<PaginationResult<Entity.Shipment>, Entity.FilterAdvancedPresentationRequest>({
            query: (request) => {
                const queryParams: Record<string, any> = Object.assign({}, filteredFilter(request.list))
                return ({
                    url: `/${ SHIPMENT_PATH }/find/advancedPresentation?${new URLSearchParams(queryParams).toString()}`,
                    method: 'POST',
                    data: request.filter,
                })
            },
            providesTags: ['Shipments'],
        }),
        
        insertShipment: builder.mutation<Entity.Shipment, Entity.CreateShipment>({
            query: (toInsert) => {
                return ({
                    url: `/${ SHIPMENT_PATH }`,
                    method: 'POST',
                    data: toInsert,
                })
            },
            onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
                const { data: mutationData } = await queryFulfilled;
                dispatch(
                    shipmentApi.util.upsertQueryData('getShipment', Entity.PrimaryShipment.fromId(mutationData.id), mutationData),
                );
            },
            invalidatesTags: ['Shipments'],
        }),
        insertMultipleShipment: builder.mutation<Entity.Shipment[], Entity.CreateShipment[]>({
            query: (toInsert) => {
                return ({
                    url: `/${ SHIPMENT_PATH }/batch`,
                    method: 'POST',
                    data: toInsert,
                })
            },
            onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
                const { data: mutationData } = await queryFulfilled;
                for(const item of mutationData) {
                    dispatch(
                            shipmentApi.util.upsertQueryData('getShipment', Entity.PrimaryShipment.fromId(item.id), item),
                    );
                }
            },
            invalidatesTags: ['Shipments'],
        }),
        updateShipment: builder.mutation<Entity.Shipment, Entity.UpdateEntityShipment>({
            query: (toUpdate) => {
                return ({
                    url: `/${ SHIPMENT_PATH }/${toUpdate.id.toPath()}`,
                    method: 'PUT',
                    data: toUpdate.data,
                })
            },
            invalidatesTags: ['Shipments'],
        }),
        updateMultipleShipment: builder.mutation<Entity.Shipment[], Entity.UpdateEntityShipment[]>({
            query: (toUpdate) => {
                return ({
                    url: `/${ SHIPMENT_PATH }/batch`,
                    method: 'PUT',
                    data: toUpdate,
                })
            },
            invalidatesTags: ['Shipments'],
        }),
        deleteShipment: builder.mutation<Entity.Shipment, Entity.Shipment>({
            query: (toDelete) => {
                return ({
                    url: `/${ SHIPMENT_PATH }/${Entity.PrimaryShipment.fromEntity(toDelete)?.toPath()}`,
                    method: 'DELETE',
                })
            },
            invalidatesTags: ['Shipments'],
        }),
        deleteMultipleShipment: builder.mutation<Entity.Shipment[], Entity.PrimaryShipment[]>({
            query: (ids) => {
                return ({
                    url: `/${ SHIPMENT_PATH }/batch${Entity.PrimaryShipment.toQueryParams(ids)}`,
                    method: 'DELETE',
                })
            },
            invalidatesTags: ['Shipments'],
        }),
    }),
})

export const [shipmentNormalizer, shipmentNormalizerMiddleware] = createNormalizationMiddleware(SHIPMENT_API_REDUCER_KEY, shipmentApi, {
    devLogging: properties.debugLog,
})

export const {
    useGetShipmentListQuery,
    useGetShipmentMultipleQuery,
    useGetShipmentPresentationQuery,
    useGetShipmentQuery,
    useInsertShipmentMutation,
    useInsertMultipleShipmentMutation,
    useUpdateShipmentMutation,
    useUpdateMultipleShipmentMutation,
    useDeleteShipmentMutation,
    useDeleteMultipleShipmentMutation,
    useGetShipmentAdvancedPresentationQuery,
    
} = shipmentApi
